import React, { useState, useEffect, useMemo } from 'react';
import { useForm, FormProvider, FieldPath } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { BoxV2 as Box, Flex, Wizard } from 'portal-commons';
import { formatPhoneNumberIntl } from 'react-phone-number-input';
import { useAuthContext } from '@/contexts';
import { SelectItem } from '@/utils/types';
import { RootReducer } from '@/store/types';
import {
  getCountryOptions,
  getStockExchangeOptions,
  getUsStateOptions,
} from '@/apis/services/enums';
import {
  ValidationRuleController,
  profileController,
  CspProfilePayload,
} from '@/apis/core';
import { ValidationRule } from '@/apis/core/types';
import { toastFlashMessage } from '@/utils';

import {
  StepRegister,
  StepCspDetails,
  StepContactDetails,
  StepTermsConditions,
  Divider,
} from '../components';
import {
  WizardSteps,
  EXCLUDED_COUNTRIES,
  DefaultRegisterFormData,
  DefaultDetailsFormData,
  DefaultContactFormData,
} from '../constants';
import { CspRegistrationFormData } from '../types';
import {
  preFillFromToken,
  syncFromProfile,
  emailArrayToString,
} from '../helpers';

const CspRegistrationContainer: React.FC = () => {
  const { profile } = useAuthContext();
  const history = useHistory();
  const [activeStep, setActiveStep] = useState('register');
  const [countryOptions, setCountryOptions] = useState<SelectItem[]>([]);
  const [stockExchangeOptions, setStockExchangeOptions] = useState<
    SelectItem[]
  >([]);
  const [usStateOptions, setUsStateOptions] = useState<SelectItem[]>([]);
  const [validationRules, setValidationRules] = useState<ValidationRule[]>([]);
  const [loading, setLoading] = useState(false);

  // Redux data for payment info
  const cspData = useSelector(
    (state: RootReducer) => state.shareReducer.cspData
  );
  const paymentInfo = useMemo(() => {
    return cspData?.find((item) => item.lookup_key === 'csp-payment-info');
  }, [cspData]);

  // Initialize form with step-specific default values defined in constants
  const methods = useForm<CspRegistrationFormData>({
    mode: 'onChange',
    defaultValues: {
      register: DefaultRegisterFormData,
      details: DefaultDetailsFormData,
      contact: DefaultContactFormData,
      ui: {
        has2ndCnp: '',
        confirmCsp: false,
        phoneCountry: '',
        agreeTerms: false,
        termsAcceptDate: '',
      },
    },
  });

  useEffect(() => {
    document.title = 'The Campaign Registry - CSP Registration';

    if (profile?.status?.toLowerCase() === 'active') {
      history.push('/');
    }
  }, [profile, history]);

  useEffect(() => {
    preFillFromToken(methods.setValue);
    if (profile) {
      syncFromProfile(profile, methods.setValue);
    }
  }, [profile, methods]);

  const fetchInitialData = async () => {
    const [
      countryResult,
      stockExchangeResult,
      usStateResult,
      validationRulesResult,
    ] = await Promise.allSettled([
      getCountryOptions(),
      getStockExchangeOptions(),
      getUsStateOptions(),
      ValidationRuleController.getValidationRules({ entityType: 'CSP' }),
    ]);

    if (countryResult.status === 'fulfilled') {
      // Filter out excluded countries, sort alphabetically, and prepend US
      const filtered = countryResult.value
        .filter((opt) => !EXCLUDED_COUNTRIES.includes(opt.value))
        .sort((a, b) => a.label.localeCompare(b.label));

      const sortedCountries = [
        { value: 'US', label: 'United States' },
        ...filtered,
      ];
      setCountryOptions(sortedCountries);
    }

    if (stockExchangeResult.status === 'fulfilled') {
      setStockExchangeOptions(stockExchangeResult.value);
    }
    if (usStateResult.status === 'fulfilled') {
      const sortedStates = usStateResult.value.sort((a, b) =>
        a.label.localeCompare(b.label)
      );
      setUsStateOptions(sortedStates);
    }
    if (validationRulesResult.status === 'fulfilled') {
      setValidationRules(validationRulesResult.value);
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  const handleNext = async () => {
    const isValid = await methods.trigger(
      activeStep as FieldPath<CspRegistrationFormData>
    );
    if (isValid) {
      const currentIndex = WizardSteps.findIndex((s) => s.key === activeStep);
      if (currentIndex < WizardSteps.length - 1) {
        setActiveStep(WizardSteps[currentIndex + 1].key);
      }
    }
  };

  const handleBack = () => {
    const currentIndex = WizardSteps.findIndex((s) => s.key === activeStep);
    if (currentIndex > 0) {
      const prevStepKey = WizardSteps[currentIndex - 1].key;
      setActiveStep(prevStepKey);

      // Trigger validation for the previous step after DOM mounts
      setTimeout(() => {
        methods.trigger(prevStepKey as FieldPath<CspRegistrationFormData>);
      }, 0);
    }
  };

  const prepareCspProfilePayload = (
    data: CspRegistrationFormData
  ): CspProfilePayload => {
    const { register, details, contact, ui } = data;

    const formattedPhone =
      formatPhoneNumberIntl(contact.phone) || contact.phone;

    const payload: CspProfilePayload = {
      ...details,
      ...register,
      phone: formattedPhone,
      email: emailArrayToString(contact.email),
      financeEmail: emailArrayToString(contact.financeEmail),
      techEmail: emailArrayToString(contact.techEmail),
      escalateEmail: emailArrayToString(contact.escalateEmail),
      termsAndConditionsAcceptDate: ui.termsAcceptDate,
      displayName: details.displayName || details.companyName,
    };

    // Clear secondary CNP fields if not applicable
    if (ui.has2ndCnp === 'no') {
      delete payload.questionnaireSecondaryCnpName;
      delete payload.questionnaireRelationshipLengthSecondaryCNP;
    }

    return payload;
  };

  const onSubmit = async (data: CspRegistrationFormData) => {
    setLoading(true);
    try {
      const payload = prepareCspProfilePayload(data);
      const result = await profileController.createCspProfile(payload);

      if (result) {
        toastFlashMessage(
          'CSP registration request submitted successfully',
          'success'
        );
        history.push('/user/csp/status');
      }
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
          if (activeStep !== 'terms') {
            e.preventDefault();
            handleNext();
          } else {
            methods.handleSubmit(onSubmit)(e);
          }
        }}
        style={{ height: '100%', width: '100%' }}
      >
        <Flex
          sx={{
            height: activeStep === 'register' ? '740px' : '800px',
            width: '860px',
            margin: '40px auto 0',
            flexDirection: 'column',
            border: '1px solid #CCCFD0',
            borderRadius: '8px',
            overflow: 'hidden', // Hide outer scroll
            boxSizing: 'border-box',
            backgroundColor: '#fff',
          }}
        >
          <Box
            sx={{
              pt: 24,
              pb: 20, // Wizard text height
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            <Wizard steps={WizardSteps} activeKey={activeStep} />
          </Box>
          <Divider sx={{ my: 24, mx: 'auto', width: 'calc(100% - 48px)' }} />

          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden', // Scrolling handled by step components
            }}
          >
            {activeStep === 'register' && <StepRegister />}
            {activeStep === 'details' && (
              <StepCspDetails
                onBack={handleBack}
                countryOptions={countryOptions}
                stockExchangeOptions={stockExchangeOptions}
                usStateOptions={usStateOptions}
                validationRules={validationRules}
              />
            )}
            {activeStep === 'contact' && (
              <StepContactDetails onBack={handleBack} />
            )}
            {activeStep === 'terms' && (
              <StepTermsConditions
                onBack={handleBack}
                paymentInfo={paymentInfo}
                loading={loading}
              />
            )}
          </Box>
        </Flex>
      </form>
    </FormProvider>
  );
};

export default CspRegistrationContainer;
